/* global google */
import React, {
  Children,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import { useMap } from '@vis.gl/react-google-maps/dist/index.umd'

export const PANE_NAMES = {
  FLOAT_PANE: 'floatPane',
  MAP_PANE: 'mapPane',
  MARKER_LAYER: 'markerLayer',
  OVERLAY_LAYER: 'overlayLayer',
  OVERLAY_MOUSE_TARGET: 'overlayMouseTarget',
}

const ensureLatLng = (position) => {
  if (!position) return null
  return position instanceof google.maps.LatLng
    ? position
    : new google.maps.LatLng(position.lat, position.lng)
}

const getOffsetOverride = (containerElement, getPixelPositionOffset) => {
  if (typeof getPixelPositionOffset !== 'function' || !containerElement) {
    return { x: 0, y: 0 }
  }
  return getPixelPositionOffset(
    containerElement.offsetWidth,
    containerElement.offsetHeight
  )
}

const getLayoutStyles = (mapCanvasProjection, offset, position) => {
  if (!mapCanvasProjection || !position) {
    return { left: '-9999px', top: '-9999px' }
  }

  const point = mapCanvasProjection.fromLatLngToDivPixel(position)

  if (!point) {
    return { left: '-9999px', top: '-9999px' }
  }

  return {
    left: `${point.x + offset.x}px`,
    top: `${point.y + offset.y}px`,
  }
}

const areStylesEqual = (current, next) =>
  current.left === next.left &&
  current.top === next.top &&
  current.width === next.width &&
  current.height === next.height &&
  current.zIndex === next.zIndex

const OverlayViewComponent = forwardRef(
  (
    {
      children,
      mapPaneName = PANE_NAMES.FLOAT_PANE,
      position,
      getPixelPositionOffset,
      zIndex,
      onLoad,
      onUnmount,
    },
    ref
  ) => {
    const map = useMap()
    const containerRef = useRef(null)
    const [overlayView, setOverlayView] = useState(null)
    const [paneEl, setPaneEl] = useState(null)
    const [containerStyle, setContainerStyle] = useState({
      position: 'absolute',
    })

    const latLngPosition = useMemo(() => ensureLatLng(position), [position])

    const updatePane = useCallback(
      (currentOverlay) => {
        if (!currentOverlay) return
        const panes = currentOverlay.getPanes()

        if (panes && mapPaneName && panes[mapPaneName]) {
          setPaneEl(panes[mapPaneName])
        } else {
          setPaneEl(null)
        }
      },
      [mapPaneName]
    )

    const onPositionElement = useCallback(
      (currentOverlay) => {
        if (!currentOverlay || !latLngPosition) return

        const projection = currentOverlay.getProjection()
        const offset = getOffsetOverride(
          containerRef.current,
          getPixelPositionOffset
        )
        const layoutStyles = getLayoutStyles(
          projection,
          offset,
          latLngPosition
        )

        const nextStyle = {
          position: 'absolute',
          top: layoutStyles.top || 0,
          left: layoutStyles.left || 0,
        }

        if (zIndex !== undefined) {
          nextStyle.zIndex = zIndex
        }

        setContainerStyle((prev) => (areStylesEqual(prev, nextStyle) ? prev : nextStyle))
      },
      [getPixelPositionOffset, latLngPosition, zIndex]
    )

    useEffect(() => {
      if (!map) return undefined

      const overlay = new google.maps.OverlayView()
      overlay.setMap(map)
      setOverlayView(overlay)

      return () => {
        overlay.setMap(null)
        setPaneEl(null)
        setOverlayView(null)
      }
    }, [map])

    useEffect(() => {
      if (!overlayView) return

      overlayView.onAdd = () => {
        updatePane(overlayView)
        if (onLoad) onLoad(overlayView)
      }

      overlayView.draw = () => {
        onPositionElement(overlayView)
      }

      overlayView.onRemove = () => {
        setPaneEl(null)
        if (onUnmount) onUnmount(overlayView)
      }

      updatePane(overlayView)
      overlayView.draw()
    }, [overlayView, updatePane, onPositionElement, onLoad, onUnmount])

    useEffect(() => {
      if (!overlayView) return
      overlayView.draw()
    }, [overlayView, latLngPosition, getPixelPositionOffset, zIndex])

    useImperativeHandle(ref, () => overlayView, [overlayView])

    if (!paneEl) return null

    return ReactDOM.createPortal(
      <div ref={containerRef} style={containerStyle}>
        {Children.only(children)}
      </div>,
      paneEl
    )
  }
)

OverlayViewComponent.FLOAT_PANE = PANE_NAMES.FLOAT_PANE
OverlayViewComponent.MAP_PANE = PANE_NAMES.MAP_PANE
OverlayViewComponent.MARKER_LAYER = PANE_NAMES.MARKER_LAYER
OverlayViewComponent.OVERLAY_LAYER = PANE_NAMES.OVERLAY_LAYER
OverlayViewComponent.OVERLAY_MOUSE_TARGET = PANE_NAMES.OVERLAY_MOUSE_TARGET

export const OverlayView = OverlayViewComponent
export default OverlayViewComponent

